import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  input,
  OnDestroy,
  signal,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import EasyMDE, { type ToolbarIcon } from 'easymde';

const TOOLBAR_BUTTONS: readonly ToolbarIcon[] = [
  {
    name: 'bold',
    action: EasyMDE.toggleBold,
    className: 'markdown-toolbar-button',
    title: 'Bold',
    icon: '<span class="material-icons" aria-hidden="true">format_bold</span>'
  },
  {
    name: 'italic',
    action: EasyMDE.toggleItalic,
    className: 'markdown-toolbar-button',
    title: 'Italic',
    icon: '<span class="material-icons" aria-hidden="true">format_italic</span>'
  },
  {
    name: 'unordered-list',
    action: EasyMDE.toggleUnorderedList,
    className: 'markdown-toolbar-button',
    title: 'Bulleted list',
    icon: '<span class="material-icons" aria-hidden="true">format_list_bulleted</span>'
  },
  {
    name: 'ordered-list',
    action: EasyMDE.toggleOrderedList,
    className: 'markdown-toolbar-button',
    title: 'Numbered list',
    icon: '<span class="material-icons" aria-hidden="true">format_list_numbered</span>'
  },
  {
    name: 'link',
    action: EasyMDE.drawLink,
    className: 'markdown-toolbar-button',
    title: 'Insert link',
    icon: '<span class="material-icons" aria-hidden="true">link</span>'
  },
  {
    name: 'preview',
    action: EasyMDE.togglePreview,
    className: 'markdown-toolbar-button',
    title: 'Preview',
    noDisable: true,
    icon: '<span class="material-icons" aria-hidden="true">preview</span>'
  }
];

@Component({
  selector: 'app-markdown-editor',
  standalone: true,
  template: `
    <div class="markdown-editor-shell" [class.is-disabled]="isDisabled()">
      <textarea #editorTextarea class="markdown-editor-textarea"></textarea>
    </div>
  `,
  styleUrl: './markdown-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MarkdownEditorComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarkdownEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild('editorTextarea', { static: true }) private editorTextarea!: ElementRef<HTMLTextAreaElement>;

  readonly placeholder = input('');
  readonly minHeight = input('220px');

  private editor: EasyMDE | null = null;
  private value = '';
  private isApplyingExternalValue = false;
  readonly isDisabled = signal(false);

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngAfterViewInit(): void {
    this.editor = new EasyMDE({
      autoDownloadFontAwesome: false,
      autofocus: false,
      element: this.editorTextarea.nativeElement,
      forceSync: true,
      initialValue: this.value,
      indentWithTabs: false,
      minHeight: this.minHeight(),
      placeholder: this.placeholder(),
      spellChecker: false,
      status: false,
      toolbar: this.buildToolbar()
    });

    this.editor.codemirror.on('change', this.handleEditorChange);
    this.editor.codemirror.on('blur', this.handleEditorBlur);
    this.applyDisabledState();
  }

  ngOnDestroy(): void {
    if (!this.editor) {
      return;
    }

    this.editor.codemirror.off('change', this.handleEditorChange);
    this.editor.codemirror.off('blur', this.handleEditorBlur);
    this.editor.cleanup();
    this.editor.toTextArea();
    this.editor = null;
  }

  writeValue(value: string | null): void {
    const normalizedValue = value ?? '';
    this.value = normalizedValue;

    if (!this.editor || this.editor.value() === normalizedValue) {
      return;
    }

    this.isApplyingExternalValue = true;
    this.editor.value(normalizedValue);
    this.isApplyingExternalValue = false;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    this.applyDisabledState();
  }

  private readonly handleEditorChange = () => {
    if (!this.editor || this.isApplyingExternalValue) {
      return;
    }

    const nextValue = this.editor.value();
    this.value = nextValue;
    this.onChange(nextValue);
  };

  private readonly handleEditorBlur = () => {
    this.onTouched();
  };

  private applyDisabledState(): void {
    if (!this.editor) {
      return;
    }

    this.editor.codemirror.setOption('readOnly', this.isDisabled() ? 'nocursor' : false);
  }

  private buildToolbar(): ToolbarIcon[] {
    return [...TOOLBAR_BUTTONS];
  }
}